import { createAuthWebClient } from "@no-busy-work/auth/web";
import type { PublicAuthSession } from "@no-busy-work/auth/protocol";
import type { CreateIncidentRequest, IncidentApprovalTransitionRequest, IncidentCommunicationTransitionRequest, IncidentDetailResponse, IncidentListFilters, IncidentListResponse, ReopenIncidentRequest } from "@no-busy-work/ops-bridge-protocol";
import type { ActionRow, HealthResponse } from "../features/ops-center/OpsCenter.Types";
import { OPERATOR_HEADERS, SOURCE_ARTIFACT } from "../features/ops-center/OpsCenter.Config";
import { authSession, requestJson } from "../lib/api";

const authClient = createAuthWebClient({ baseUrl: "/" });

export const opsApi = {
  getSession: () => authClient.getSession(),
  login: (accessCode: string) => authClient.login<PublicAuthSession>({ accessCode, returnTo: "/" }),
  logout: (session: PublicAuthSession | null) => authClient.logout(authSession(session)),
  getHealth: () => requestJson<HealthResponse>(authClient, "/health"),
  listIncidents: (session: PublicAuthSession | null, filters: IncidentListFilters) =>
    requestJson<IncidentListResponse>(authClient, incidentListPath(filters), { session }),
  loadIncident: (session: PublicAuthSession | null, incidentId: string) =>
    requestJson<IncidentDetailResponse>(authClient, `/api/incidents/${encodeURIComponent(incidentId)}`, { session }),
  createIncident: (session: PublicAuthSession | null, body: CreateIncidentRequest) =>
    requestJson<IncidentDetailResponse>(authClient, "/api/incidents", {
      method: "POST",
      body,
      csrfToken: authenticatedCsrf(session),
      session
    }),
  recordReadinessGate: (session: PublicAuthSession | null, incidentId: string, gateId: string, evidence: CloseoutEvidenceRequest = {}) =>
    requestJson<IncidentDetailResponse>(authClient, `/api/incidents/${encodeURIComponent(incidentId)}/readiness-gates/${encodeURIComponent(gateId)}`, {
      method: "POST",
      body: evidence,
      csrfToken: authenticatedCsrf(session),
      session
    }),
  transitionCommunication: (session: PublicAuthSession | null, incidentId: string, body: IncidentCommunicationTransitionRequest) =>
    requestJson<IncidentDetailResponse>(authClient, `/api/incidents/${encodeURIComponent(incidentId)}/communications`, {
      method: "POST",
      body,
      csrfToken: authenticatedCsrf(session),
      session
    }),
  transitionApproval: (session: PublicAuthSession | null, incidentId: string, body: IncidentApprovalTransitionRequest) =>
    requestJson<IncidentDetailResponse>(authClient, `/api/incidents/${encodeURIComponent(incidentId)}/approvals`, {
      method: "POST",
      body,
      csrfToken: authenticatedCsrf(session),
      session
    }),
  resolveIncident: (session: PublicAuthSession | null, incidentId: string, rootCause: string, customerSafeSummary?: string) =>
    requestJson<IncidentDetailResponse>(authClient, `/api/incidents/${encodeURIComponent(incidentId)}/resolve`, {
      method: "POST",
      body: { rootCause, customerSafeSummary },
      csrfToken: authenticatedCsrf(session),
      session
    }),
  reopenIncident: (session: PublicAuthSession | null, incidentId: string, body: ReopenIncidentRequest) =>
    requestJson<IncidentDetailResponse>(authClient, `/api/incidents/${encodeURIComponent(incidentId)}/reopen`, {
      method: "POST",
      body,
      csrfToken: authenticatedCsrf(session),
      session
    }),
  invokeAction: (session: PublicAuthSession | null, detail: IncidentDetailResponse["detail"], row: ActionRow) =>
    requestJson<unknown>(authClient, row.path, {
      method: "POST",
      body: actionBody(detail, row),
      csrfToken: authenticatedCsrf(session),
      session,
      headers: {
        [OPERATOR_HEADERS.Actor]: operatorActorRef(session),
        [OPERATOR_HEADERS.Tenant]: detail.incident.tenantId,
        [OPERATOR_HEADERS.Authority]: SOURCE_ARTIFACT
      }
    })
};

interface CloseoutEvidenceRequest {
  readonly rootCause?: string | null;
  readonly remediationProof?: string | null;
  readonly verificationEvidence?: string | null;
  readonly customerSafeSummary?: string | null;
  readonly actionId?: string | null;
}

function incidentListPath(filters: IncidentListFilters): string {
  const query = new URLSearchParams();
  appendQuery(query, "search", filters.search);
  appendQuery(query, "appId", filters.appId);
  appendQuery(query, "environment", filters.environment);
  appendQuery(query, "severity", filters.severity);
  appendQuery(query, "status", filters.status);
  appendQuery(query, "service", filters.service);
  appendQuery(query, "sort", filters.sort);
  const serialized = query.toString();
  return serialized ? `/api/incidents?${serialized}` : "/api/incidents";
}

function appendQuery(query: URLSearchParams, name: string, value: string | null | undefined): void {
  const text = value?.trim();
  if (text && text !== "all") {
    query.set(name, text);
  }
}

function authenticatedCsrf(session: PublicAuthSession | null): string | undefined {
  return session?.authenticated ? session.csrfToken : undefined;
}

function operatorActorRef(session: PublicAuthSession | null): string {
  if (!session?.authenticated) {
    return "ops-center-browser";
  }
  return session.actor.subject || session.actor.displayName;
}

function actionBody(detail: IncidentDetailResponse["detail"], row: ActionRow): Record<string, unknown> {
  if (row.endpointKind === "code_bridge_session") {
    return {
      tenant_id: detail.incident.tenantId,
      environment: graphEnvironment(detail.incident.environment),
      incident_id: firstUuid([detail.incident.id]) ?? nilUuid("2"),
      target_node_id: firstUuid([detail.signals[0]?.id, detail.evidence[0]?.id]) ?? nilUuid("1"),
      proposal_id: row.proposalId,
      repo_ref: "No-Busy-Work/nbw-ops-bridge",
      project_ref: "ops-center-control-plane",
      validation_command_ref: "ops_bridge.validation.default",
      session_kind: "remediation",
      requested_work_policy_class: row.policyClass,
      evidence_ids: uuidList(detail.evidence.map((item) => item.id)),
      credential_ref_ids: [],
      readiness_check_ids: [],
      requested_at: new Date().toISOString(),
      source_artifact: SOURCE_ARTIFACT
    };
  }
  return {
    tenant_id: detail.incident.tenantId,
    environment: graphEnvironment(detail.incident.environment),
    target_node_id: firstUuid([detail.signals[0]?.id, detail.evidence[0]?.id]) ?? nilUuid("1"),
    gate_id: row.gateId,
    action_ref: row.actionRef,
    arguments: {},
    evidence_ids: uuidList(detail.evidence.map((item) => item.id)),
    credential_ref_ids: [],
    readiness_check_ids: [],
    requested_at: new Date().toISOString(),
    source_artifact: SOURCE_ARTIFACT
  };
}

function graphEnvironment(value: string): "production" | "preview" | "local" {
  if (value === "production" || value === "preview" || value === "local") {
    return value;
  }
  return "production";
}

function uuidList(values: readonly string[]): readonly string[] {
  return values.filter((value) => isUuid(String(value)));
}

function firstUuid(values: readonly (string | undefined)[]): string | null {
  return values.find((value) => value !== undefined && isUuid(value)) ?? null;
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value);
}

function nilUuid(suffix: string): string {
  return `00000000-0000-4000-8000-00000000000${suffix}`;
}
